import classNames from 'classnames';
import { FC } from 'react';

import { RosterRequestStatus } from '../generated/graphql';
import { useAppSelector } from '../store/store';
import getEnumLabel from '../utilities/getEnumLabel';

type BadgeColor = 'YELLOW' | 'GREEN' | 'BLUE' | 'PINK' | 'RED' | 'GRAY';
type BadgeSize = 'sm' | 'md';

type BadgeProps = {
  children: React.ReactNode;
  color: BadgeColor;
  size?: BadgeSize;
  noDot?: boolean;
};

const BadgeBase: FC<BadgeProps> = ({ children, color, size = 'sm', noDot }) => {
  const hasDot = !noDot;
  let dotClass;
  let bgClass;
  let textClass;

  switch (color) {
    case 'YELLOW':
      dotClass = 'bg-yellow-400';
      bgClass = 'bg-yellow-100';
      textClass = 'text-yellow-800';
      break;
    case 'GREEN':
      dotClass = 'bg-green-400';
      bgClass = 'bg-green-100';
      textClass = 'text-green-800';
      break;
    case 'BLUE':
      dotClass = 'bg-blue-400';
      bgClass = 'bg-blue-100';
      textClass = 'text-blue-800';
      break;
    case 'PINK':
      dotClass = 'bg-pink-400';
      bgClass = 'bg-pink-100';
      textClass = 'text-pink-800';
      break;
    case 'RED':
      dotClass = 'bg-red-400';
      bgClass = 'bg-red-100';
      textClass = 'text-red-800';
      break;
    case 'GRAY':
      dotClass = 'bg-gray-400';
      bgClass = 'bg-gray-100';
      textClass = 'text-gray-800';
      break;
    default:
      break;
  }

  // Size-based styling
  const sizeClasses = {
    sm: {
      container: 'px-2.5 py-0.5',
      dot: 'size-1.5',
      text: 'text-xs',
      spacing: 'ml-3',
    },
    md: {
      container: 'px-3 py-1.5',
      dot: 'size-2',
      text: 'text-sm',
      spacing: 'ml-4',
    },
  };

  const currentSize = sizeClasses[size];

  return (
    <div
      className={`relative inline-flex items-center rounded-full ${bgClass} ${currentSize.container}`}
    >
      {hasDot && (
        <span className="absolute flex shrink-0 items-center justify-center">
          <span
            className={`${currentSize.dot} rounded-full ${dotClass}`}
            aria-hidden="true"
          />
        </span>
      )}
      <span
        className={classNames(`${currentSize.text} font-medium`, textClass, {
          [currentSize.spacing]: hasDot,
        })}
      >
        {children}
      </span>
    </div>
  );
};

type RequestStatusBadgeProps = {
  status: RosterRequestStatus;
  size?: BadgeSize;
};

const RequestStatusBadge: FC<RequestStatusBadgeProps> = ({
  status,
  size = 'sm',
}) => {
  const { rosterRequestStatus } = useAppSelector((state) => state.enums);

  let color = 'YELLOW' as BadgeColor;

  switch (status) {
    case RosterRequestStatus.RequestReceived:
      color = 'GRAY';
      break;
    case RosterRequestStatus.InProgress:
    case RosterRequestStatus.AwaitingConfirmation:
      color = 'BLUE';
      break;
    case RosterRequestStatus.MoreInfoRequired:
    case RosterRequestStatus.OverPdApprovalNeeded:
    case RosterRequestStatus.CancellationRequested:
      color = 'YELLOW';
      break;
    case RosterRequestStatus.Confirmed:
      color = 'GREEN';
      break;
    case RosterRequestStatus.Canceled:
      color = 'RED';
      break;
    default:
      color = 'GRAY';
  }

  return (
    <BadgeBase color={color} size={size}>
      {getEnumLabel(rosterRequestStatus, status)}
    </BadgeBase>
  );
};

export { BadgeBase, RequestStatusBadge };
